"use client"

import { useState, useCallback, useMemo } from "react"
import type { CodeItem } from "@/types/code-values"

export type AtsSyncStatusFilter = "all" | "enabled" | "disabled"

interface CodeValueDeps {
  getCodeValuesSortByText: (groupName: string, required: boolean) => CodeItem[]
  getRelatedCodeValues: (groupName: string, parentId: number, parentGroup: string) => CodeItem[]
  getCodeValueCode: (id: number, groupName: string) => string
}

export function useAtsSyncSettingsFilters({ getCodeValuesSortByText, getRelatedCodeValues, getCodeValueCode }: CodeValueDeps) {
  const [status, setStatus] = useState<AtsSyncStatusFilter>("all")
  const [warningsOnly, setWarningsOnly] = useState(false)
  const [unmappedOnly, setUnmappedOnly] = useState(false)
  const [countryId, setCountryId] = useState<number | null>(null)
  const [subdivisionId, setSubdivisionId] = useState<number | null>(null)
  const [city, setCity] = useState("")

  const countries = useMemo(
    () => getCodeValuesSortByText("geo.CodeCountry", true),
    [getCodeValuesSortByText],
  )

  const subdivisions = useMemo(
    () => countryId !== null ? getRelatedCodeValues("geo.CodeSubdivision", countryId, "geo.CodeCountry") : [],
    [getRelatedCodeValues, countryId],
  )

  const setCountry = useCallback((id: number | null) => {
    setCountryId(id)
    setSubdivisionId(null)
  }, [])

  const clear = useCallback(() => {
    setStatus("all")
    setWarningsOnly(false)
    setUnmappedOnly(false)
    setCountryId(null)
    setSubdivisionId(null)
    setCity("")
  }, [])

  const activeCount = useMemo(
    () => [status !== "all", warningsOnly, unmappedOnly, countryId !== null, subdivisionId !== null, city.trim() !== ""].filter(Boolean).length,
    [status, warningsOnly, unmappedOnly, countryId, subdivisionId, city],
  )

  const formatClientAddress = useCallback(
    (client: { countryId?: number | null; subdivisionId?: number | null; city?: string | null }): string | null => {
      const parts: string[] = []
      if (client.city) parts.push(client.city)
      if (client.subdivisionId != null) {
        const code = getCodeValueCode(client.subdivisionId, "geo.CodeSubdivision")
        const shortCode = code.split("-")[1] ?? code
        if (shortCode) parts.push(shortCode)
      }
      if (client.countryId != null) {
        const countryCode = getCodeValueCode(client.countryId, "geo.CodeCountry")
        if (countryCode) parts.push(countryCode)
      }
      return parts.length > 0 ? parts.join(", ") : null
    },
    [getCodeValueCode],
  )

  return {
    status,
    setStatus,
    warningsOnly,
    setWarningsOnly,
    unmappedOnly,
    setUnmappedOnly,
    countryId,
    setCountry,
    subdivisionId,
    setSubdivisionId,
    city,
    setCity,
    countries,
    subdivisions,
    activeCount,
    clear,
    formatClientAddress,
  }
}

export type AtsSyncSettingsFilters = ReturnType<typeof useAtsSyncSettingsFilters>
